/**
 * Products Query Hooks（商品管理）
 * 封裝產品列表查詢，用於線上商店／線上訂單（商品來源＝商品管理，與面交預購區分）
 */

import { useProductsList } from '@pos/api-sdk';
import { getApiHeaders, getTenantIdFromUrl } from '../../api/apiUtils';
import { useTenant } from '../../contexts/TenantContext';
import { swrListConfig } from '../../lib/swr-config';
import type { SWRConfiguration } from 'swr';
import type { ProductsListParams } from '@pos/api-sdk';

/**
 * 取得商品列表（商品管理 API）
 * 用於 /online-shopping、/online-order 等與前台 POS 同源之商品
 */
export function useProducts(
  params?: ProductsListParams,
  options?: {
    fetch?: RequestInit;
    revalidateOnFocus?: boolean;
    dedupingInterval?: number;
    enabled?: boolean;
    swr?: SWRConfiguration;
  }
) {
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const {
    revalidateOnFocus = true,
    dedupingInterval = 2000,
    enabled = true,
    swr: swrOptions,
  } = options ?? {};

  const apiHeaders = getApiHeaders(tenantId);
  const fetchOptions: RequestInit = {
    ...options?.fetch,
    headers: {
      ...apiHeaders,
      ...(options?.fetch?.headers || {}),
    },
  };

  const result = useProductsList(params, {
    fetch: fetchOptions,
    swr: {
      ...swrListConfig,
      enabled: enabled && !!tenantId,
      revalidateOnFocus,
      dedupingInterval,
      ...swrOptions,
    },
  });

  const listData = result.data?.data;
  const products =
    listData && 'data' in listData && Array.isArray(listData.data)
      ? listData.data
      : undefined;
  const pagination =
    listData && 'pagination' in listData ? listData.pagination : undefined;

  return {
    products,
    pagination,
    isLoading: result.isLoading,
    isError: !!result.error,
    error: result.error,
    mutate: result.mutate,
    isValidating: result.isValidating,
  };
}
